// Count-up: animate any [data-countup] figure to its value once it scrolls into
// view. The element's text IS the final value (e.g. "150 000", "100+", "3–5"), so
// the target(s) and formatting are parsed straight from it — nothing to keep in
// sync with the CMS.
//
// Each figure is tokenised into number-segments and literal separators. EVERY
// number animates (in parallel, on the same eased clock), while the literals —
// range dashes ("3–5"), units ("+", "%") — stay put. A thousands space inside a
// number ("150 000") is kept as part of that one number, not treated as a break.
//
// Works for both stat blocks: grid-statistics marks just the number span, grid-
// impact marks the whole label; both are handled the same way.

const DURATION = 1600;
// One number: a run of digits with optional inner thousands-spacing, or a lone
// digit. A range dash isn't a digit/space, so it splits "3–5" into two numbers.
const NUMBER = /\d[\d\s ]*\d|\d/g;

// Group thousands with the number's own separator (space / nbsp) so intermediate
// frames match the final rendering exactly.
function group(value, sep) {
	return Math.round(value)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

// Split the raw text into ordered segments: {num, target, sep} for numbers,
// {text} for the literals between/around them. Returns null when there's no
// number to animate (plain text label).
function plan(raw) {
	const segs = [];
	let last = 0;
	let m;
	NUMBER.lastIndex = 0;
	while ((m = NUMBER.exec(raw))) {
		if (m.index > last) segs.push({ text: raw.slice(last, m.index) });
		const tok = m[0];
		const sepMatch = tok.match(/\d([\s ])\d/);
		segs.push({
			num: true,
			target: parseInt(tok.replace(/[\s ]/g, ""), 10),
			sep: sepMatch ? sepMatch[1] : " ",
		});
		last = m.index + tok.length;
	}
	if (last < raw.length) segs.push({ text: raw.slice(last) });
	return segs.some((s) => s.num) ? segs : null;
}

function render(segs, eased) {
	return segs
		.map((s) => (s.num ? group(s.target * eased, s.sep) : s.text))
		.join("");
}

function animate(el, raw, segs) {
	const start = performance.now();
	function tick(now) {
		const t = Math.min((now - start) / DURATION, 1);
		const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
		if (t < 1) {
			el.textContent = render(segs, eased);
			requestAnimationFrame(tick);
		} else {
			el.textContent = raw; // exact original string, always
		}
	}
	requestAnimationFrame(tick);
}

export function initCountUp() {
	const els = document.querySelectorAll("[data-countup]");
	if (!els.length) return;

	const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	const targets = [];

	els.forEach((el) => {
		if (el.dataset.countupReady) return;
		const raw = el.textContent.trim();
		const segs = plan(raw);
		if (!segs) return; // no number to count — leave it alone
		el.dataset.countupReady = "1";
		if (reduce) return; // keep the final value, no animation
		el.textContent = render(segs, 0); // hold at 0 off-screen
		targets.push({ el, raw, segs });
	});

	if (reduce || !targets.length) return;

	const io = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				io.unobserve(entry.target);
				const found = targets.find((t) => t.el === entry.target);
				if (found) animate(found.el, found.raw, found.segs);
			});
		},
		{ threshold: 0.4 }
	);

	targets.forEach(({ el }) => io.observe(el));
}

if (document.readyState !== "loading") initCountUp();
else document.addEventListener("DOMContentLoaded", initCountUp);
